import { useState, useEffect, useCallback } from "react";
import { api, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, RefreshCw, Loader2, History, CheckCircle2 } from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { formatTanggal } from "@/lib/tanggal";

/**
 * Rekon Unit vs Kartu Stok — brief owner 31 Agu 2026 poin 2 & 4.
 *
 * Membandingkan TIGA buku yang harus bergerak bersama: registrasi unit ber-IMEI,
 * lapisan biaya (nilai), dan kartu stok (kuantitas). Ketiganya bisa berpisah
 * jalan tanpa satu pun pesan galat — sudah terjadi dua kali di sistem ini.
 *
 * Angka yang paling penting di halaman ini adalah "selisih = 0": brief poin 4
 * menjadikannya SYARAT MUTLAK sebelum migrasi data.
 */
export default function RekonUnitStok() {
  const { can } = useAuth();
  const bolehJalankan = can("inventory_unit", "update");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [riwayat, setRiwayat] = useState([]);
  const [tampilCocok, setTampilCocok] = useState(false);

  const muat = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/rekon-unit");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat rekonsiliasi");
    } finally { setLoading(false); }
  }, []);

  const muatRiwayat = useCallback(async () => {
    try {
      const r = await api.get("/inventory/rekon-unit/riwayat", { params: { limit: 14 } });
      setRiwayat(r.data.items || []);
    } catch { /* riwayat opsional — halaman tetap berguna tanpanya */ }
  }, []);

  useEffect(() => { muat(); muatRiwayat(); }, [muat, muatRiwayat]);

  const jalankanSekarang = async () => {
    setBusy(true);
    try {
      const r = await api.post("/inventory/rekon-unit/jalankan");
      toast.success(r.data?.message || "Rekon selesai");
      await Promise.all([muat(), muatRiwayat()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menjalankan rekon");
    } finally { setBusy(false); }
  };

  const r = data?.ringkas;
  const nol = r?.selisih_nol;

  return (
    <div className="p-4 md:p-8 max-w-[1400px] space-y-6" data-testid="rekon-unit-stok">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Inventory</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 inline-flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-600" /> Rekon Unit vs Kartu Stok
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Membandingkan tiga catatan yang harus selalu sama: <strong>unit ber-IMEI</strong> yang
            terdaftar, <strong>nilai persediaan</strong>, dan <strong>kartu stok</strong>.
            Berjalan otomatis tiap malam; tombol di samping menghitung ulang sekarang juga.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={muat} disabled={loading} data-testid="rekon-muat-ulang"
                  className="h-9 px-3 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Hitung Ulang
          </button>
          {bolehJalankan && (
            <button onClick={jalankanSekarang} disabled={busy} data-testid="rekon-jalankan"
                    className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <History className="w-4 h-4" />}
              Simpan ke Riwayat
            </button>
          )}
        </div>
      </header>

      {/* Kartu vonis. Brief poin 4 menjadikan "nol" syarat mutlak migrasi, jadi
          jawabannya harus terbaca dalam satu pandangan — bukan disimpulkan
          sendiri dari tabel di bawah. */}
      {r && (
        <div className={`rounded-xl border p-4 ${nol ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}
             data-testid="rekon-vonis">
          <div className="flex items-start gap-3">
            {nol ? <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                 : <AlertTriangle className="w-6 h-6 text-rose-600 shrink-0" />}
            <div>
              <p className={`font-display font-bold text-lg ${nol ? "text-emerald-900" : "text-rose-900"}`}>
                {nol ? "Selisih NOL — data siap dipakai sebagai dasar pencatatan baru"
                     : `${r.selisih} baris BERSELISIH — belum siap untuk migrasi`}
              </p>
              <p className={`text-xs mt-0.5 ${nol ? "text-emerald-800" : "text-rose-800"}`}>
                {r.baris_diperiksa} baris diperiksa · {r.cocok} cocok · {r.selisih} berselisih
                {r.dikecualikan > 0 && <> · {r.dikecualikan} dikecualikan (penampungan trade-in)</>}
              </p>
              <p className="text-[11px] text-slate-500 mt-1">
                Dihitung {data?.dihitung_pada ? formatTanggal(data.dihitung_pada, { denganJam: true }) : "-"}
              </p>
            </div>
          </div>
        </div>
      )}

      <LoadingState loading={loading} kosong={!loading && !data} pesanKosong="Belum ada data rekonsiliasi.">
        {data && (
          <>
            {data.selisih?.length > 0 && (
              <section data-testid="rekon-tabel-selisih">
                <h2 className="font-display font-bold text-slate-900 mb-2">Baris berselisih</h2>
                <TabelRekon rows={data.selisih} sorot />
              </section>
            )}

            {data.dikecualikan?.length > 0 && (
              <section className="mt-6" data-testid="rekon-tabel-dikecualikan">
                <h2 className="font-display font-bold text-slate-900 mb-1">Dikecualikan</h2>
                <p className="text-xs text-slate-600 mb-2">
                  Penampungan unit trade-in yang belum diklasifikasi. Sengaja tidak dihitung
                  sebagai selisih — tapi tetap ditampilkan supaya tidak hilang dari pandangan.
                </p>
                <TabelRekon rows={data.dikecualikan} />
              </section>
            )}

            <section className="mt-6">
              <button onClick={() => setTampilCocok(v => !v)} data-testid="rekon-toggle-cocok"
                      className="text-sm text-emerald-700 hover:text-emerald-800 font-medium">
                {tampilCocok ? "Sembunyikan" : "Tampilkan"} {data.cocok?.length || 0} baris yang sudah cocok
              </button>
              {tampilCocok && <div className="mt-2"><TabelRekon rows={data.cocok} /></div>}
            </section>
          </>
        )}
      </LoadingState>

      {riwayat.length > 0 && (
        <section className="mt-8" data-testid="rekon-riwayat">
          <h2 className="font-display font-bold text-slate-900 mb-1">Riwayat rekon malam</h2>
          <p className="text-xs text-slate-600 mb-2">
            Yang penting di sini adalah ARAHnya. Selisih yang membesar dari hari ke hari berarti
            masih ada keran yang terbuka — bukan sekadar sisa lama yang belum dibereskan.
          </p>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left py-2.5 px-4">Tanggal</th>
                  <th className="text-left py-2.5 px-4">Status</th>
                  <th className="text-right py-2.5 px-4">Diperiksa</th>
                  <th className="text-right py-2.5 px-4">Cocok</th>
                  <th className="text-right py-2.5 px-4">Berselisih</th>
                </tr>
              </thead>
              <tbody>
                {riwayat.map(h => (
                  <tr key={h.id} className="border-b border-slate-100" data-testid={`rekon-riwayat-${h.tanggal}`}>
                    <td className="py-2.5 px-4 font-mono text-xs">{h.tanggal}</td>
                    <td className="py-2.5 px-4 text-xs">{h.status}</td>
                    <td className="py-2.5 px-4 text-right">{h.ringkas?.baris_diperiksa ?? "-"}</td>
                    <td className="py-2.5 px-4 text-right text-emerald-700">{h.ringkas?.cocok ?? "-"}</td>
                    <td className={`py-2.5 px-4 text-right font-semibold ${h.ringkas?.selisih ? "text-rose-700" : "text-emerald-700"}`}>
                      {h.ringkas?.selisih ?? "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function TabelRekon({ rows, sorot }) {
  if (!rows?.length) return null;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
          <tr>
            <th className="text-left py-2.5 px-4">SKU / Produk</th>
            <th className="text-left py-2.5 px-4">Gudang</th>
            <th className="text-right py-2.5 px-4">Unit</th>
            <th className="text-right py-2.5 px-4">Nilai (qty)</th>
            <th className="text-right py-2.5 px-4">Kartu Stok</th>
            <th className="text-right py-2.5 px-4">Selisih</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(b => (
            <tr key={`${b.sku}-${b.warehouse_id}`}
                className={`border-b border-slate-100 ${sorot ? "bg-rose-50/40" : ""}`}
                data-testid={`rekon-baris-${b.sku}`}>
              <td className="py-2.5 px-4">
                <span className="font-mono text-xs">{b.sku}</span>
                <span className="block text-[11px] text-slate-500">{b.nama_produk}</span>
                {b.imei?.length > 0 && (
                  <span className="block text-[10px] text-slate-400 font-mono">
                    IMEI: {b.imei.slice(0, 4).join(", ")}{b.unit > 4 ? ` … +${b.unit - 4}` : ""}
                  </span>
                )}
              </td>
              <td className="py-2.5 px-4 text-xs text-slate-600">{b.warehouse_nama}</td>
              <td className="py-2.5 px-4 text-right font-semibold">{b.unit}</td>
              <td className="py-2.5 px-4 text-right">
                {b.lapisan}
                {/* Nilai modal hanya dikirim server untuk pemegang izin laporan. */}
                {b.nilai_lapisan != null && (
                  <span className="block text-[10px] text-slate-400">{idr(b.nilai_lapisan)}</span>
                )}
              </td>
              <td className="py-2.5 px-4 text-right">{b.kartu}</td>
              <td className="py-2.5 px-4 text-right text-xs">
                {b.selisih_lapisan !== 0 && (
                  <span className="block text-amber-700">nilai {b.selisih_lapisan > 0 ? "+" : ""}{b.selisih_lapisan}</span>
                )}
                {b.selisih_kartu !== 0 && (
                  <span className="block text-rose-700">kartu {b.selisih_kartu > 0 ? "+" : ""}{b.selisih_kartu}</span>
                )}
                {b.cocok && <span className="text-emerald-700">cocok</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
